import AppHttpClient from '../api-manager/api/http-client';
import HttpPathsEnum from '../api-manager/api/http-paths.enum';
import { HttpRequest, RequestType } from '../api-manager/api/http-request';
import { GetGuestResponseDto } from './dto/response/get-guest-response.dto';
import { UpdateGuestRequestDto } from './dto/request/edit-guest-request.dto';
import { EditGuestResponseDto } from './dto/response/edit-guest-response.dto';
import { GuardGuestsFiltersDto } from './dto/request/guard-guests-filters.dto';
import { AddNewGuestRequestDto } from './dto/request/add-new-guest.request.dto';
import { AddNewGuestResponseDto } from './dto/response/add-new-guest-response.dto';
import { GuestsFiltersDto } from '../guest-management/dto/request/guests-filters.dto';
import { GetGuestResponseGuestDto } from './dto/response/get-guest-response-guest.dto';
import { AddManyGuestsResponseDto } from '../guest-management/dto/response/add-many-guests.response.dto';
import { CreateManyGuestsRequestDto } from '../guest-management/dto/request/create-many-guests-request.dto';
import { AddNewRepeatableGuestRequestDto } from './dto/request/add-new-repeatable-guest.request.dto';
import { RepeatableGuestDto } from './dto/response/repeatable-guest.dto';
import { RepeatableGuestQueryDto } from './dto/request/repeatable-guest-query.dto';
import { GetRepeatableGuestsPaginationResponseDto } from './dto/response/get-repeatable-guests-pagination.response.dto';
import { UpdateNewRepeatableGuestRequestDto } from './dto/request/update-new-repeatable-guest.request.dto';
import { UpdateRepeatableGuestsEndDatesRequestDto } from './dto/request/update-repeatable-guests-end-date.request.dto';

const GUESTS_USER_PATH = HttpPathsEnum.USERS;

class GuestManagementApi {
  public async createRepeatableGuest(guest: AddNewRepeatableGuestRequestDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/repeatable-guest`,
      method: RequestType.POST,
      data: guest,
    };

    return await AppHttpClient.exec<any, RepeatableGuestDto>(
      request,
      RepeatableGuestDto
    );
  }

  public async updateRepeatableGuestsEndDates(
    data: UpdateRepeatableGuestsEndDatesRequestDto
  ) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/repeatable-guests-end-date`,
      method: RequestType.PATCH,
      data,
    };

    return await AppHttpClient.exec<any, RepeatableGuestDto>(
      request,
      RepeatableGuestDto
    );
  }

  public async getRepeatableGuestsByQuery(query: RepeatableGuestQueryDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/repeatable-guests/all`,
      method: RequestType.GET,
      params: {
        ...query,
      },
    };

    return await AppHttpClient.exec<
      any,
      GetRepeatableGuestsPaginationResponseDto
    >(request, GetRepeatableGuestsPaginationResponseDto);
  }

  public async updateRepeatableGuest(
    id: number,
    guest: UpdateNewRepeatableGuestRequestDto
  ) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/repeatable-guests/${id}`,
      method: RequestType.PATCH,
      data: guest,
    };

    return await AppHttpClient.exec<any, boolean>(request, Boolean);
  }

  public async removeRepeatableGuest(id: number) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/repeatable-guests/${id}`,
      method: RequestType.DELETE,
    };

    return await AppHttpClient.exec<any, boolean>(request, Boolean);
  }

  public async getAllFutureGuestsByUserId(guestsFilters: GuestsFiltersDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/resident/future`,
      method: RequestType.GET,
      params: {
        ...guestsFilters,
      },
    };

    return await AppHttpClient.exec<any, GetGuestResponseDto>(
      request,
      GetGuestResponseDto
    );
  }

  public async getAllPastGuestsByUserId(guestsFilters: GuestsFiltersDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/resident/past`,
      method: RequestType.GET,
      params: {
        ...guestsFilters,
      },
    };

    return await AppHttpClient.exec<any, GetGuestResponseDto>(
      request,
      GetGuestResponseDto
    );
  }

  public async getAllGuardFutureGuests(guardFilters: GuardGuestsFiltersDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/guard-all-guests/future`,
      method: RequestType.GET,
      params: {
        ...guardFilters,
      },
    };

    return await AppHttpClient.exec<any, GetGuestResponseDto>(
      request,
      GetGuestResponseDto
    );
  }

  public async getAllGuardPastGuests(guardFilters: GuardGuestsFiltersDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/guard-all-guests/past`,
      method: RequestType.GET,
      params: {
        ...guardFilters,
      },
    };

    return await AppHttpClient.exec<any, GetGuestResponseDto>(
      request,
      GetGuestResponseDto
    );
  }

  public async removeGuest(id: number) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/${id}`,
      method: RequestType.DELETE,
    };

    return await AppHttpClient.exec<any, any>(request, {});
  }

  public async addNewGuest(data: AddNewGuestRequestDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests`,
      method: RequestType.POST,
      data,
    };

    return await AppHttpClient.exec<
      AddNewGuestRequestDto,
      AddNewGuestResponseDto
    >(request, AddNewGuestResponseDto);
  }

  public async updateGuest(id: number, data: UpdateGuestRequestDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/${id}`,
      method: RequestType.PATCH,
      data,
    };

    return await AppHttpClient.exec<
      UpdateGuestRequestDto,
      EditGuestResponseDto
    >(request, EditGuestResponseDto);
  }

  public async getGuestById(id: number) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/${id}`,
      method: RequestType.GET,
    };

    return await AppHttpClient.exec<any, GetGuestResponseGuestDto>(
      request,
      GetGuestResponseGuestDto
    );
  }

  public async addManyGuestsFromFile(data: CreateManyGuestsRequestDto) {
    const request: HttpRequest = {
      url: `${GUESTS_USER_PATH}/guests/add-many-guests`,
      method: RequestType.POST,
      data: data.file,
    };

    return await AppHttpClient.exec<any, AddManyGuestsResponseDto>(
      request,
      AddManyGuestsResponseDto
    );
  }
}

export default new GuestManagementApi();
